import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalLink, canonicalUrl, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { AUSTIN_SIBLINGS } from "@/data/austinSiblings";
import hero from "@/assets/original/stock-traditional.jpg";
import heroPicture from "@/assets/original/stock-traditional.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";

const PATH = "/patio-covers/in/austin";
const TITLE = "Patio Covers in Austin, TX | Texas Made Shade";
const DESC = "Custom patio covers in Austin, TX — welded aluminum frames, Sunbrella, Phifertex Plus and insulated tops. Built in Seguin, installed across Travis, Williamson and Hays Counties. Free on-site quotes.";

export const Route = createFileRoute("/patio-covers/in/austin")({
  head: (ctx) => ({
    links: [canonicalLink(PATH)],
    meta: routeMeta(ctx, {
      title: TITLE,
      description: DESC,
      image: hero,
      imageAlt: "Custom welded patio cover installed at an Austin, TX backyard",
      socialTitle: "Patio Covers in Austin, TX",
      socialDescription: DESC,
    }),
    scripts: [
      ...serviceHeadScripts({
        path: PATH,
        serviceName: "Patio Covers in Austin, TX",
        breadcrumbName: "Patio Covers in Austin",
        description: DESC,
        faqs: [
          { q: "How much does a patio cover cost in Austin?", a: "Most residential aluminum patio covers in Austin run $4,800–$10,500 depending on span, fabric vs solid top, and attachment to the home. Hill-country installs that need engineered footings sit on the higher end. Every quote is written, itemized and free." },
          { q: "Do I need a permit for a patio cover in Austin?", a: "Inside Austin city limits attached patio covers usually require a residential permit through the Development Services Department and an HOA review. We pull permits and provide stamped wind-load drawings; in Lakeway, Bee Cave, Westlake and unincorporated Travis County the rules vary — we'll tell you what's needed before we quote." },
          { q: "How long does it take to build a patio cover in Austin?", a: "From signed quote to installed cover most Austin jobs run 3–5 weeks. Fabrication happens in our Seguin shop so the on-site install is typically a single day." },
          { q: "What kind of warranty comes with your patio covers?", a: "Welded aluminum frames carry a 20-year structural warranty. Sunbrella canvas tops carry a 10-year Sunbrella warranty plus our 2-year workmanship warranty." },
        ],
      }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Patio Covers in Austin, TX",
          serviceType: "Patio Cover Installation",
          url: canonicalUrl(PATH),
          provider: { "@id": "https://texasmadeshade.com/#business" },
          areaServed: { "@type": "City", name: "Austin", containedInPlace: { "@type": "AdministrativeArea", name: "Travis County" } },
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <ServicePage
      currentPath={PATH}
      serviceName="Patio Covers in Austin"
      breadcrumbs={[
        { label: "Patio & Pergola Covers", to: "/pergola-covers" },
        { label: "Patio Covers in Austin, TX" },
      ]}
      eyebrow="★ PATIO COVERS · AUSTIN, TX ★"
      title="Custom Patio Covers in Austin, TX"
      intro="From Westlake to Mueller to Circle C, we design, weld and install custom patio covers built for Austin sun and Hill Country wind — attached covers, freestanding pavilions, outdoor-kitchen shade and full pergola roofs. Built in our Seguin shop, installed by the same crew."
      heroImage={heroPicture}
      heroCta={{
        eyebrow: "Patio Cover Builder · Serving Austin, TX",
        headline: "Austin Patio Cover Quote — Free On-Site Estimate",
        subhead: "Send a few photos of your Austin backyard and rough dimensions. We'll come back with a written estimate from a real person.",
        primaryHref: `/#lets-talk?type=${encodeURIComponent("Patio / Pergola Cover")}&notes=${encodeURIComponent("Patio cover quote — project in Austin, TX.")}`,
        primaryLabel: "Get My Austin Patio Cover Quote",
        phoneNumber: "+1 (830) 390-9588",
      }}
      features={[
        "Welded aluminum frames — no rust, no rot",
        "Attached, freestanding, gable & flat designs",
        "Sunbrella, Phifertex Plus & insulated tops",
        "Engineered for sustained Hill Country wind loads",
        "Permits & HOA drawings for City of Austin jobs",
        "Serving Travis, Williamson & Hays Counties",
      ]}
      body={[
        {
          heading: "Why Austin Patios Need More Than a Wood Pergola",
          text: "Cedar and pine pergolas split, sag and turn grey inside two summers under Central Texas sun. Our powder-coated aluminum frames don't splinter, don't host termites and don't need staining every spring. The Sunbrella tops we sew in Seguin hold their color 8–12 years even in full Austin sun.",
        },
        {
          heading: "Designs Built for Austin Homes",
          text: "We build attached covers that tie cleanly into the stone, stucco and Hardie siding common on Westlake, Tarrytown and Barton Creek homes. Freestanding pavilions sit over pools, hot tubs and outdoor kitchens without touching the house — useful on Lakeway and Lake Travis lots where setbacks and view corridors matter.",
          cta: { label: "See our pergola cover options", href: "/pergola-covers" },
        },
        {
          heading: "Outdoor Kitchens, Pools & Grill Islands",
          text: "Putting in a built-in grill or smoker? We size shade to keep the cook out of direct sun without trapping heat — usually a vented gable with a Phifertex Plus top or an insulated Snap-N-Lock panel when full rain protection matters around Austin's spring storms.",
          cta: { label: "Pool & spa shade", href: "/pool-spa" },
        },
        {
          heading: "Permits, HOA & Engineering",
          text: "Inside Austin city limits we pull the residential permit and provide stamped wind-load drawings for your HOA submission. In Lakeway, Bee Cave, Cedar Park, Round Rock and unincorporated Travis County rules vary — we tell you up front what's required before we quote.",
        },
      ]}
      applications={[
        "Backyard Patio Covers",
        "Outdoor Kitchen Shade",
        "Pool Cabanas",
        "Front Porch Covers",
        "Carport & Side-Yard Covers",
        "Drop Curtains & Wind Walls",
        "Replacement Pergola Tops",
      ]}
      faqs={[
        { q: "How much does a patio cover cost in Austin?", a: "Most residential aluminum patio covers in Austin run $4,800–$10,500 depending on span, fabric vs solid top, and attachment to the home. Hill country installs requiring engineered footings sit on the higher end." },
        { q: "Do I need a permit for a patio cover in Austin?", a: "Inside city limits attached patio covers usually require a residential permit and HOA review. We pull permits and provide stamped drawings; in surrounding jurisdictions rules vary." },
        { q: "How long does install take?", a: "From signed quote to installed cover most Austin jobs run 3–5 weeks. Fabrication is in our Seguin shop so on-site install is typically a single day." },
        { q: "Aluminum vs. wood pergola — which holds up in Austin?", a: "Aluminum every time. Wood splinters, sags and needs staining every 2–3 years. Welded aluminum is powder-coated, never rusts and lasts 20+ years with zero maintenance." },
      ]}
      cityCallouts={[
        { to: "/austin", city: "Austin", anchorText: "All Texas Made Shade services in Austin, TX", blurb: "See every awning, cover, sail-shade and welding service we offer to Austin." },
        { to: "/pergola-covers/in/round-rock", city: "Round Rock", anchorText: "Patio covers in Round Rock, TX", blurb: "Williamson County patio and pergola cover installs." },
        { to: "/pergola-covers/in/cedar-park", city: "Cedar Park", anchorText: "Patio covers in Cedar Park, TX", blurb: "Cedar Park and Leander patio cover installs." },
      ]}
      postCta={<AustinSiblingCallouts current={PATH} />}
    />
  );
}

export function AustinSiblingCallouts({ current }: { current: string }) {
  const others = AUSTIN_SIBLINGS.filter((s) => s.to !== current);
  return (
    <section className="border-t-4 border-navy-deep bg-white py-16">
      <div className="mx-auto max-w-6xl px-4 lg:px-8">
        <div className="text-center">
          <div className="font-display text-sm tracking-[0.3em] text-red">★ MORE AUSTIN SHADE SERVICES ★</div>
          <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">All Our Austin Landing Pages</h2>
        </div>
        <ul className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {others.map((s) => (
            <li key={s.to} className="border-l-4 border-red bg-cream p-5">
              <Link to={s.to} className="font-display text-lg font-bold uppercase tracking-wide text-navy-deep hover:text-red">
                {s.label}
              </Link>
              <p className="mt-2 text-sm text-navy-deep/80">{s.blurb}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
